// Atomic multi-file commit to a GitHub repo via the Git Data API.
// Browser-direct — api.github.com is CORS-friendly for authenticated
// requests, so no server sits in the path (06_PUBLISH_PIPELINE.md).
//
// Adapted from agicore-foundry's github-sync.ts::pushSnapshot. Strategy:
// snapshot the whole desired tree in one commit (blob → tree → commit →
// move ref). base_tree is omitted, so the commit is a full replacement
// of the repo root — exactly right for the public repo, whose entire
// content IS the rendered site. Files absent from the new tree get
// removed (e.g. an unpublished post's HTML disappears).

import { GithubError } from "./github";
import type { SiteFile } from "./site-render";

const API = "https://api.github.com";
const BRANCH = "main";

async function gh<T>(token: string, method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = `${method} ${path} failed`;
    try {
      const j = JSON.parse(text) as { message?: string };
      if (j.message) msg = j.message;
    } catch { /* non-JSON */ }
    throw new GithubError(msg, res.status, text);
  }
  return (text ? JSON.parse(text) : {}) as T;
}

export interface CommitResult {
  oid: string;
  url: string;
  fileCount: number;
}

/** Commit `files` as the complete tree of `owner/repo`'s main branch.
 *  Returns the new commit. Idempotent by content — committing an
 *  unchanged tree produces the same tree sha and effectively a no-op
 *  commit. */
export async function commitFiles(
  token: string,
  ownerRepo: string,
  files: SiteFile[],
  message: string,
): Promise<CommitResult> {
  const [owner, name] = ownerRepo.split("/");
  if (!owner || !name) throw new Error(`invalid repo "${ownerRepo}" — expected "<owner>/<name>"`);
  if (files.length === 0) throw new Error("nothing to commit");

  // 1. Parent commit sha. The repo was auto_init'd at provision time,
  //    so main already exists; handle the empty cases defensively.
  let parentSha: string | null = null;
  try {
    const ref = await gh<{ object: { sha: string } }>(token, "GET", `/repos/${owner}/${name}/git/refs/heads/${BRANCH}`);
    parentSha = ref.object.sha;
  } catch (e) {
    if (!(e instanceof GithubError) || (e.status !== 404 && e.status !== 409)) throw e;
    // Empty repo — seed the branch so the Git Data API will accept blobs.
    await gh(token, "PUT", `/repos/${owner}/${name}/contents/.bka-init`, {
      message: "BKA: initialize public repo",
      content: btoa(".bka\n"),
      branch: BRANCH,
    });
    const ref = await gh<{ object: { sha: string } }>(token, "GET", `/repos/${owner}/${name}/git/refs/heads/${BRANCH}`);
    parentSha = ref.object.sha;
  }

  // 2. Blob per file (utf-8 — GitHub accepts raw text, no base64 needed).
  const blobs: Array<{ path: string; sha: string }> = [];
  for (const f of files) {
    const blob = await gh<{ sha: string }>(token, "POST", `/repos/${owner}/${name}/git/blobs`, {
      content: f.content,
      encoding: "utf-8",
    });
    blobs.push({ path: f.path, sha: blob.sha });
  }

  // 3. Tree (base_tree omitted = full root replacement = clean snapshot).
  const tree = await gh<{ sha: string }>(token, "POST", `/repos/${owner}/${name}/git/trees`, {
    tree: blobs.map((b) => ({ path: b.path, mode: "100644", type: "blob", sha: b.sha })),
  });

  // 4. Commit.
  const me = await gh<{ login: string; email: string | null; name: string | null }>(token, "GET", "/user");
  const author = {
    name: me.name || me.login,
    email: me.email || `${me.login}@users.noreply.github.com`,
  };
  const commit = await gh<{ sha: string }>(token, "POST", `/repos/${owner}/${name}/git/commits`, {
    message,
    tree: tree.sha,
    parents: parentSha ? [parentSha] : [],
    author,
    committer: author,
  });

  // 5. Move the ref.
  if (parentSha) {
    await gh(token, "PATCH", `/repos/${owner}/${name}/git/refs/heads/${BRANCH}`, { sha: commit.sha, force: false });
  } else {
    await gh(token, "POST", `/repos/${owner}/${name}/git/refs`, { ref: `refs/heads/${BRANCH}`, sha: commit.sha });
  }

  return {
    oid: commit.sha,
    url: `https://github.com/${owner}/${name}/commit/${commit.sha}`,
    fileCount: files.length,
  };
}
